// src/pages/dashboard/branch/menu-items/components/AddMenuItemModal.tsx
import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Button from "../../../../../../components/ui/Button";
import {
  MenuItem,
  Category,
  insertMenuItem,
  ItemVariant,
} from "../../../../../../utils/supabaseClient";
import { uploadToSupabaseStorage } from "../../../../../../utils/supabaseStorage";
import { SpiceLevel } from "../../../../../../types/restaurant";
import { ItemIngredientsState } from "../../../../../../types/ingredient";
import BasicInformationTab from "./tabs/BasicInformationTab";
import AdditionalDetailsTab from "./tabs/AdditionalDetailsTab";
import ImageSettingsTab from "./tabs/ImageSettingsTab";
import LabelsTab from "./tabs/LabelsTab";
import DynamicOptions from "./tabs/DynamicOptions";
import DietaryPreferencesTab from "./tabs/DietaryPreferencesTab";
import { createNewMenuItem, fetchMenuItems } from "../../../../../../store/slices/menuItemSlice";
import { ingredientService } from "../../../../../../services/ingredientService";
import { useAppDispatch } from "../../../../../../store/hooks";

interface Option {
  name: string;
  maxedit: number;
  isMultipleSelection: boolean;
  isVariant: boolean;
  imageUrl?: string;
  children: { name: string; price: number; imageUrl?: string, description: string }[];
}

interface AddMenuItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMenuItemAdded: (menuItem: MenuItem) => void;
  branchId: string;
  categories: Category[];
  selectedCategoryId?: string;
}

export interface FormData {
  name: string;
  description: string;
  price: string;
  discountedPrice: string;
  categoryId: string;
  isPublished: boolean;
  isAvailable: boolean;
  isFeatured: boolean;
  calories: string;
  preparationTime: string;
  spiceLevel: SpiceLevel;
  tags: number[];
  options: ItemVariant[];
}

export interface ArFormData {
  name: string;
  description: string;
}

const tabs = [
  { id: "basic", key: "basicInformation" },
  { id: "details", key: "additionalDetails" },
  { id: "options", key: "options" },
  // { id: "labels", key: "labels" },
  { id: "dietary", key: "dietaryPreferences" },
  { id: "image", key: "imageAndSettings" },
];

const AddMenuItemModal: React.FC<AddMenuItemModalProps> = ({
  isOpen,
  onClose,
  onMenuItemAdded,
  branchId,
  categories,
  selectedCategoryId,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState("basic");
  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
    price: "",
    discountedPrice: "",
    categoryId: selectedCategoryId || "",
    isPublished: true,
    isAvailable: true,
    isFeatured: false,
    calories: "",
    preparationTime: "",
    spiceLevel: "none" as SpiceLevel,
    tags: [],
    options: [],
  });

  const [arFormData, setArFormData] = useState<ArFormData>({
    name: "",
    description: "",
  });

  const [ingredients, setIngredients] = useState<string[]>([]);
  const [arIngredients, setArIngredients] = useState<string[]>([]);
  const [structuredIngredients, setStructuredIngredients] = useState<ItemIngredientsState>({
    individual_ingredients: [],
    ingredient_groups: [],
  });
  const [selectedFoodTags, setSelectedFoodTags] = useState<number[]>([]);
  const [selectedDietaryPreferences, setSelectedDietaryPreferences] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<ItemVariant[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSubmittingRef = useRef(false);

  const validateForm = () => {
    const newErrors: string[] = [];

    if (!formData.name.trim()) {
      newErrors.push(t("menuItems.errors.nameRequired"));
    }

    if (
      !formData.price ||
      isNaN(Number(formData.price)) ||
      Number(formData.price) <= 0
    ) {
      newErrors.push(t("menuItems.errors.invalidPrice"));
    }

    if (
      formData.discountedPrice &&
      (isNaN(Number(formData.discountedPrice)) ||
        Number(formData.discountedPrice) < 0)
    ) {
      newErrors.push(t("menuItems.errors.invalidDiscountedPrice"));
    }

    if (
      formData.discountedPrice &&
      Number(formData.discountedPrice) >= Number(formData.price)
    ) {
      newErrors.push(t("menuItems.errors.discountedPriceTooHigh"));
    }


    if (!formData.categoryId) {
      newErrors.push(t("menuItems.errors.categoryRequired"));
    }

    // Note: Variant validation is now handled by the DynamicOptions component

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingRef.current || isLoading) {
      return;
    }
    setError(null);

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors[0]);
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);

    try {
      // Upload image if provided
      let imageUrl = null;
      if (imageFile) {
        const uniqueFileName = `${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}-${imageFile.name}`;
        const uploadResult = await uploadToSupabaseStorage(
          imageFile,
          uniqueFileName,
          {
            bucket: "uploads",
            path: "menu-items",
          }
        );
        imageUrl = uploadResult.publicUrl;
      }

      const dataToSave = {
        name: formData.name,
        description: formData.description,
        price: Number(formData.price),
        category_id: formData.categoryId,
        branch_id: branchId,
        is_published: formData.isPublished,
        is_available: formData.isAvailable,
        is_featured: formData.isFeatured,
        discounted_price: formData.discountedPrice
          ? Number(formData.discountedPrice)
          : null,
        image_url: imageUrl,

        spice_level: formData.spiceLevel,
        calories: formData.calories ? Number(formData.calories) : null,
        preparation_time: formData.preparationTime
          ? Number(formData.preparationTime)
          : null,
        ar: {
          name: arFormData.name || null,
          description: arFormData.description || null,
          ingredients: arIngredients.length > 0 ? arIngredients : null,
        },
        tags: selectedFoodTags,
        options: [], // Dynamic variants are handled with branch_action_config_id and override_data arrays
        selected_dietary: selectedDietaryPreferences,
        ingredients: structuredIngredients.individual_ingredients,
        groups: structuredIngredients.ingredient_groups,

        // Add variant configuration arrays (only if variants are selected)
        // These arrays are parallel: each index corresponds to the same variant
        // Example: 
        //   branch_action_config_id: ["config1", "config2"] 
        //   override_data: [null, { price_behavior: "increase", name: "Extra Cheese", price: 2 }]
        ...(selectedOptions.length > 0 && {
          branch_action_config_id: selectedOptions.map(variant => variant.branch_action_config_id),
          override_data: selectedOptions.map(variant => variant.override_data),
        }),
      };

      // Debug logging for variant data
      if (selectedOptions.length > 0) {
        console.log('Saving item with variants:', {
          branch_action_config_id: selectedOptions.map(v => v.branch_action_config_id),
          override_data: selectedOptions.map(v => v.override_data),
          selectedOptions: selectedOptions
        });
      }

      const createdItem = await dispatch(createNewMenuItem(dataToSave)).unwrap();

      if (createdItem?.id && (
        structuredIngredients.individual_ingredients.length > 0 || structuredIngredients.ingredient_groups.length > 0
      )) {
        try {
          await ingredientService.removeAllItemIngredients(createdItem.id);

          for (const ingredientId of structuredIngredients.individual_ingredients) {
            await ingredientService.addItemIngredient({
              item_id: createdItem.id,
              ingredient_id: ingredientId,
            });
          }

          for (const groupId of structuredIngredients.ingredient_groups) {
            await ingredientService.addItemIngredient({
              item_id: createdItem.id,
              group_id: groupId,
            });
          }
        } catch (syncError) {
          console.error("Failed to sync structured ingredients", syncError);
        }
      }

      // Redux already updated the state optimistically
      // But notify parent to refresh paginated data (MenuItemsPage uses server-side pagination)
      onMenuItemAdded(createdItem);

      handleClose();
    } catch (err) {
      console.error("Error creating menu item:", err);
      setError(t("menuItems.errors.createFailed"));
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleClose = () => {
    isSubmittingRef.current = false;
    setIsLoading(false);
    setFormData({
      name: "",
      description: "",
      price: "",
      discountedPrice: "",
      categoryId: selectedCategoryId || "",
      isPublished: true,
      isAvailable: true,
      isFeatured: false,
      calories: "",
      preparationTime: "",
      spiceLevel: "none",
      tags: [],
      options: [],
    });
    setArFormData({
      name: "",
      description: "",
    });
    setIngredients([]);
    setArIngredients([]);
    setStructuredIngredients({ individual_ingredients: [], ingredient_groups: [] });
    setSelectedFoodTags([]);
    setSelectedDietaryPreferences([]);
    setSelectedOptions([]);
    setImageFile(null);
    setImagePreview(null);
    setError(null);
    setActiveTab("basic");
    onClose();
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case "basic":
        return (
          <BasicInformationTab
            formData={formData}
            arFormData={arFormData}
            categories={categories}
            onFormDataChange={setFormData}
            onArFormDataChange={setArFormData}
          />
        );
      case "details":
        return (
          <AdditionalDetailsTab
            formData={formData}
            ingredients={ingredients}
            arIngredients={arIngredients}
            onFormDataChange={setFormData}
            onIngredientsChange={setIngredients}
            onArIngredientsChange={setArIngredients}
            branchId={branchId}
            itemIngredients={structuredIngredients}
            onItemIngredientsChange={setStructuredIngredients}
          />
        );
      case "labels":
        return (
          <LabelsTab
            itemFormData={{
              ...formData,
              ingredients,
              id: "",
              category: categories.find(
                (cat) => cat.id === formData.categoryId
              ),
            }}
            selectedFoodTags={selectedFoodTags}
            onFoodTagsChange={setSelectedFoodTags}
          />
        );
      case "options":
        return (
          <DynamicOptions
            branchId={branchId}
            selectedVariants={selectedOptions}
            onVariantsChange={setSelectedOptions}
          />
        );
      case "dietary":
        return (
          <DietaryPreferencesTab
            branchId={branchId}
            selectedDietaryPreferences={selectedDietaryPreferences}
            onDietaryPreferencesChange={setSelectedDietaryPreferences}
          />
        );
      case "image":
        return (
          <ImageSettingsTab
            formData={formData}
            imageFile={imageFile}
            imagePreview={imagePreview}
            onFormDataChange={setFormData}
            onImageChange={setImageFile}
            onImagePreviewChange={setImagePreview}
          />
        );
      default:
        return null;
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto ">
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/25 backdrop-blur-sm"
              onClick={handleClose}
            />

            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full  mx-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t("menuItems.addItem")}
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
                >
                  <FiX size={24} />
                </button>
              </div>

              {/* Tabs Navigation */}
              <div className="flex border-b border-gray-200 dark:border-gray-700 px-6 overflow-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-3 text-nowrap text-sm font-medium border-b-2 transition-colors cursor-pointer ${activeTab === tab.id
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      }`}
                  >
                    {t(`menuItems.${tab.key}`)}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit}>
                {/* Tab Content */}
                <div className="p-6  overflow-y-auto">
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {error}
                      </p>
                    </div>
                  )}

                  {renderActiveTab()}
                </div>

                {/* Actions */}
                <div className="flex gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isLoading}
                    // onClick={handleSubmit}
                    disabled={
                      isLoading ||
                      isSubmittingRef.current ||
                      !formData.name.trim() ||
                      !formData.price ||
                      !formData.categoryId
                    }
                    className="flex-1"
                    rightIcon={isRTL ? <FiChevronLeft /> : <FiChevronRight />}
                  >
                    {t("menuItems.addItem")}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddMenuItemModal;
